import { format, isBefore } from 'date-fns';
import { TeamDTO } from '../dtos/Team.dto';
import { ApplicationDTO } from '../../../applications/domain/interfaces/ApplicationDTO';
import { AnnouncedTeamsMap } from '../interfaces/AnnouncedTeamsMap';
import { AnnouncedTeam } from '../interfaces/AnnouncedTeam';
import { AnnouncedTeamStatus } from '../enums/AnnouncedTeamStatus';
import { PlayerApplication } from '../interfaces/PlayerApplication';
import { ApplicationStatus } from '../../../applications/domain/enums/ApplicationStatus';

function mapApplicationStatusToAnnouncedTeamStatus(appStatus: ApplicationStatus): AnnouncedTeamStatus {
  switch (appStatus) {
    case ApplicationStatus.DECLINED:
      return AnnouncedTeamStatus.DECLINED;
    case ApplicationStatus.ACCEPTED:
      return AnnouncedTeamStatus.ACCEPTED;
    case ApplicationStatus.PENDING:
      return AnnouncedTeamStatus.PENDING;
    default:
      return AnnouncedTeamStatus.ANNOUNCED;
  }
}

function mapChampionshipTeamToAnnouncedTeam(championshipId: number, team:TeamDTO, existingApplications: ApplicationDTO[]): AnnouncedTeam {
  const applications = (team.members || [])
    .filter((member) => !existingApplications.some((app) => app.user.id === member.user.id))
    .map((member) => ({
      id: (new Date()).getTime(),
      championshipId,
      teamId: team.id,
      status: ApplicationStatus.PENDING,
      createdAt: team.createdAt ? team.createdAt: new Date().toISOString(),
      position: member.position,
      number: member.number,
      player: {
        id: member.user.id,
        firstName: member.user.name,
        lastName: member.user.surname,
        logoUrl: member.user.photo?.externalUrl,
      }
    } as PlayerApplication));

  return {
    id: team.id,
    name: team.name,
    logo: team.emblem
      ? {
        id: team.emblem.id,
        url: team.emblem.externalUrl
      }
      : undefined,
    createdAt: team.createdAt ? format(new Date(team.createdAt),'dd.MM.yyyy') : undefined,
    status: AnnouncedTeamStatus.ANNOUNCED,
    applications,
    isNew: false,
  };
}

export function mapAnnouncedChampionshipTeams(
  championshipId: number,
  teams: TeamDTO[],
  applications: ApplicationDTO[],
) {
  const result = teams.reduce<AnnouncedTeamsMap>((result, team) => ({
    ...result,
    [team.id]: mapChampionshipTeamToAnnouncedTeam(championshipId, team, applications),
  }), {});

  applications.forEach((app) => {
    const teamId = app.team.id;
    const status = app.state;
    const createdAt = new Date(app.createdAt);

    if (!result[teamId]) {
      result[teamId] = {
        id: app.team.id,
        name: app.team.name,
        logo: app.team.emblem
          ? {
            id: app.team.emblem.id,
            url: app.team.emblem.externalUrl,
          }
          : undefined,
        createdAt: app.team.createdAt ? format(new Date(app.team.createdAt),'dd.MM.yyyy') : undefined,
        status: AnnouncedTeamStatus.ANNOUNCED,
        applications: [],
        isNew: false,
      };
    }

    const announcedTeam = result[teamId];
    const applicationDate = announcedTeam.applicationDate ? new Date(announcedTeam.applicationDate) : new Date();

    const needUpdateDate = !announcedTeam.applicationDate || isBefore(createdAt, applicationDate);
    const isNotAcceptedOrDeclined = [AnnouncedTeamStatus.ACCEPTED, AnnouncedTeamStatus.DECLINED].includes(announcedTeam.status);
    const isNotDeclined = announcedTeam.status !== AnnouncedTeamStatus.DECLINED;
    const isAnnouncedOnly = announcedTeam.status === AnnouncedTeamStatus.ANNOUNCED;
    const needUpdateAnnouncedTeamStatus = (
      isAnnouncedOnly
      || (isNotAcceptedOrDeclined && [ApplicationStatus.ACCEPTED, ApplicationStatus.DECLINED].includes(status))
      || (isNotDeclined && status === ApplicationStatus.DECLINED)
    );

    const playerApplication = {
      id: app.id,
      championshipId,
      teamId,
      status,
      createdAt: createdAt.toISOString(),
      position: app.position,
      number: app.number,
      player: {
        id: app.user.id,
        firstName: app.user.name,
        lastName: app.user.surname,
        middleName: app.user.middleName,
        birthDate: app.user.birthDate,
        logoUrl: app.user.photo?.externalUrl,
      }
    } as PlayerApplication;

    announcedTeam.applications.push(playerApplication);

    if (needUpdateDate) {
      announcedTeam.applicationDate = createdAt.toISOString();
    }

    if (needUpdateAnnouncedTeamStatus) {
      announcedTeam.status = mapApplicationStatusToAnnouncedTeamStatus(status);
    }
  });

  return result;
}
